import React, { useState, useCallback, useRef, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import cn from 'classnames'

const MARGIN = 5

export default function Tooltip({ label, children }) {
  const [isActive, setIsActive] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const boxRef = useRef(null)
  const panelRef = useRef(null)

  const showTooltip = useCallback(() => setIsActive(true), [])
  const hideTooltip = useCallback(() => setIsActive(false), [])

  const updatePosition = useCallback(() => {
    if (!boxRef.current || !panelRef.current) return

    const triggerRect = boxRef.current.getBoundingClientRect()
    const panelRect = panelRef.current.getBoundingClientRect()

    let centerX = triggerRect.left + triggerRect.width / 2
    let bottomY = triggerRect.top - MARGIN

    centerX = Math.max(
      panelRect.width / 2 + MARGIN,
      Math.min(window.innerWidth - panelRect.width / 2 - MARGIN, centerX),
    )

    bottomY = Math.max(
      panelRect.height + MARGIN,
      Math.min(window.innerHeight - MARGIN, bottomY),
    )

    setPosition({ left: centerX, top: bottomY + MARGIN })
  }, [])

  useLayoutEffect(() => {
    if (!isActive) return

    const run = () => {
      if (boxRef.current && panelRef.current) updatePosition()
    }

    run()
    const raf = requestAnimationFrame(run)

    window.addEventListener('scroll', run, true)
    window.addEventListener('resize', run)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', run, true)
      window.removeEventListener('resize', run)
    }
  }, [isActive, updatePosition])

  const tooltipPanel = isActive && (
    <span
      ref={ panelRef }
      role={ 'tooltip' }
      className={ cn('tooltip-panel', 'tooltip-panel--portal', { show: true }) }
      style={ {
        position: 'fixed',
        top: position.top - MARGIN,
        left: position.left,
        transform: 'translate(-50%, -100%)',
      } }
      onMouseEnter={ showTooltip }
      onMouseLeave={ hideTooltip }
    >
      { label }
    </span>
  )

  return <>
    <div
      ref={ boxRef }
      className={ 'tooltip-box' }
      onMouseEnter={ showTooltip }
      onMouseLeave={ hideTooltip }
      onBlur={ hideTooltip }
    >
      { children }
    </div>
    { createPortal(tooltipPanel, document.body) }
  </>
}
